import React, { useEffect, useRef, useState } from "react";
import { Camera, Square, RotateCcw, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * In-browser video recorder for Tap In TV.
 * Supports Story (9:16) and Wide (16:9) modes with a safe-zone overlay.
 * Emits a File on confirm via onRecorded(file, format).
 */
export default function VideoRecorder({ format, onFormatChange, onRecorded, maxSeconds = 60 }) {
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const [isReady, setIsReady] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);

  const isStory = format === "story";
  const aspectClass = isStory ? "aspect-[9/16] max-w-[360px]" : "aspect-video max-w-3xl";

  // Request camera whenever format changes
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      setIsReady(false);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: isStory
            ? { width: { ideal: 720 }, height: { ideal: 1280 }, facingMode: "user" }
            : { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setIsReady(true);
      } catch (e) {
        toast.error("Camera access denied or unavailable");
      }
    };
    init();
    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, [isStory]);

  // Tick recording timer
  useEffect(() => {
    if (!isRecording) return;
    const iv = setInterval(() => setSeconds((s) => {
      if (s + 1 >= maxSeconds) { stop(); return maxSeconds; }
      return s + 1;
    }), 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording, maxSeconds]);

  const start = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : "video/webm";
    const rec = new MediaRecorder(streamRef.current, { mimeType });
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      setRecordedBlob(blob);
      setBlobUrl(URL.createObjectURL(blob));
    };
    rec.start();
    recorderRef.current = rec;
    setSeconds(0);
    setIsRecording(true);
  };

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const retake = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
    setRecordedBlob(null);
    setSeconds(0);
  };

  const confirm = () => {
    if (!recordedBlob) return;
    const filename = `${isStory ? "story" : "wide"}-${Date.now()}.webm`;
    const file = new File([recordedBlob], filename, { type: "video/webm" });
    onRecorded?.(file, format);
    retake();
  };

  return (
    <div data-testid="video-recorder" className="space-y-4">
      {/* Format toggle */}
      <div className="inline-flex tv-glass rounded-full p-1">
        <button
          data-testid="recorder-format-story"
          type="button"
          onClick={() => onFormatChange?.("story")}
          className={`px-4 py-1.5 text-xs font-bold rounded-full transition ${isStory ? "bg-tv-accent text-black" : "text-white/70"}`}
        >
          Story · 9:16
        </button>
        <button
          data-testid="recorder-format-wide"
          type="button"
          onClick={() => onFormatChange?.("wide")}
          className={`px-4 py-1.5 text-xs font-bold rounded-full transition ${!isStory ? "bg-tv-accent text-black" : "text-white/70"}`}
        >
          Wide · 16:9
        </button>
      </div>

      {/* Preview frame */}
      <div className={`relative mx-auto w-full ${aspectClass} bg-black rounded-2xl overflow-hidden border border-white/10`}>
        {!blobUrl ? (
          <video ref={videoRef} muted playsInline className="w-full h-full object-cover" />
        ) : (
          <video ref={previewRef} src={blobUrl} controls playsInline className="w-full h-full object-contain bg-black" />
        )}

        {/* Safe zones (dashed rule of thirds + title-safe) */}
        {!blobUrl && (
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-[5%] border border-dashed border-tv-brand/70 rounded" />
            <div className="absolute inset-[12%] border border-dashed border-white/30 rounded" />
            <div className="absolute top-3 left-3 text-[10px] uppercase tracking-widest text-tv-brand/90 font-bold">Safe zone</div>
          </div>
        )}

        {/* Rec indicator */}
        {isRecording && (
          <div className="absolute top-3 right-3 flex items-center gap-2 bg-red-600/90 text-white text-xs font-bold px-3 py-1 rounded-full">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> REC {seconds}s
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-3">
        {!blobUrl && !isRecording && (
          <button
            data-testid="recorder-start-btn"
            type="button"
            disabled={!isReady}
            onClick={start}
            className="bg-tv-live hover:bg-red-600 text-white font-bold px-6 py-3 rounded-full flex items-center gap-2 disabled:opacity-50"
          >
            <Camera className="w-4 h-4" /> Start Recording
          </button>
        )}
        {isRecording && (
          <button
            data-testid="recorder-stop-btn"
            type="button"
            onClick={stop}
            className="bg-white text-black font-bold px-6 py-3 rounded-full flex items-center gap-2"
          >
            <Square className="w-4 h-4 fill-current" /> Stop
          </button>
        )}
        {blobUrl && (
          <>
            <button
              data-testid="recorder-retake-btn"
              type="button"
              onClick={retake}
              className="tv-glass text-white font-semibold px-5 py-3 rounded-full flex items-center gap-2 border border-white/15"
            >
              <RotateCcw className="w-4 h-4" /> Retake
            </button>
            <button
              data-testid="recorder-use-btn"
              type="button"
              onClick={confirm}
              className="bg-tv-brand hover:bg-yellow-500 text-black font-bold px-6 py-3 rounded-full flex items-center gap-2"
            >
              <Check className="w-4 h-4" /> Use this recording
            </button>
          </>
        )}
      </div>
      <div className="text-xs text-white/50 text-center">Max {maxSeconds}s · Camera + mic required</div>
    </div>
  );
}
